import { baseEntityReducers, baseInitialState, EntityState } from './base/baseModelStore';
import { fetchEntitiesMap, fetchAllEntitiesMap } from './base/baseModelThunks';
import { ChamberFloodEventDetails, RecentFloodEvents } from '../api-model/models';
import { ModelReducers } from './modelReducers';
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { httpClient } from '../infrastructure/http/HttpClient';
import { AxiosResponseTransformer } from 'axios';
import store, { AppThunk } from '@root/store/store';
import { take, unionWith } from 'lodash';
import { RootState } from './rootReducer';
import { dashboardStats } from './stats';

export type EventsHistogramType = {
  [date: string]: EventsHistogramData;
};

export type EventsHistogramData = {
  floodDuration: number;
  warningDuration: number;
};

export type ChamberEvents = {
  siteId: number;
  events: ChamberFloodEventDetails[];
  histogram: EventsHistogramType;
};

export interface ActiveEntityState<T> extends EntityState<T> {
  activeEntityKey?: string;
}

const sortEventsDescending = (events: ChamberFloodEventDetails[]) =>
  events?.sort((a, b) => (a.start > b.start ? -1 : 1));

const mergeEvents = (currentEvents: ChamberFloodEventDetails[], newEvents: ChamberFloodEventDetails[]) =>
  unionWith(newEvents, currentEvents, (a, b) => a.id === b.id); // new events is passed first as when a conflict is found, first element from first array is used

function setActiveEntityKey<T>(state: ActiveEntityState<T>, action: PayloadAction<string>) {
  state.activeEntityKey = action.payload;
}

function clearActiveEntityKey<T>(state: ActiveEntityState<T>) {
  state.activeEntityKey = null;
}

const clearState = (state: ActiveEntityState<ChamberEvents>) => {
  const chambersId = Object.keys(state.entities);
  chambersId.forEach((id) => {
    delete state.entities[id];
  });
};

const updateChamberEvents = (
  state: ActiveEntityState<ChamberEvents>,
  actionPayload: PayloadAction<Partial<ChamberEvents>>
) => {
  const { siteId, ...chambersEntity } = actionPayload.payload;
  if (!siteId) return;

  const currentChamberEvents = state.entities[siteId];

  if (!currentChamberEvents) {
    state.total = +1;
  }

  const events = sortEventsDescending(mergeEvents(state.entities[siteId]?.events ?? [], chambersEntity.events ?? []));
  state.entities[siteId] = {
    siteId: siteId,
    histogram: {},
    ...state.entities[siteId],
    ...chambersEntity,
    events: events,
  };

  state.loading = false;
  state.error = null;
};

const getLatestEventsFromState = (eventState: ActiveEntityState<ChamberEvents>, totalOngoingEvents: number) => {
  if (eventState.entities) {
    const allEvents = Object.values(eventState.entities)
      .filter((e) => e.events?.length > 0)
      .flatMap((e) => e.events.filter((ev) => !ev.end));

    return {
      floodEvents: take(sortEventsDescending(allEvents), 12),
      total: totalOngoingEvents,
    };
  }

  return { floodEvents: [], total: 0 };
};

const latestEventsSelector = createSelector(
  (state: RootState) => state.events,
  (state: RootState) => state.dashboardStats.totalOngoingEvents,
  (events, totalOngoingEvents) => getLatestEventsFromState(events, totalOngoingEvents)
);

const slice = createSlice({
  name: ModelReducers.events,
  initialState: { ...baseInitialState<ChamberEvents>(), activeEntityKey: null },
  reducers: {
    ...baseEntityReducers((c) => (c as ChamberEvents).siteId.toString()),
    setActiveEntityKey,
    clearActiveEntityKey,
    updateChamberEvents,
    clearState,
  },
});

type EventsResult = {
  [id: number]: ChamberFloodEventDetails[];
};

const sortEvents = (data: EventsResult) => {
  const chambersEvents = store.getState()[ModelReducers.events];

  return Object.entries(data)
    .map(([id, events]) => ({
      siteId: id,
      histogram: {},
      ...chambersEvents.entities[id],
      events: sortEventsDescending(mergeEvents(chambersEvents.entities[id]?.events ?? [], events)),
    }))
    .reduce((acc, n) => ({ ...acc, [n.siteId]: n }), {});
};

const getActiveChamberEvent =
  (siteId: number): AppThunk =>
  async (dispatch) => {
    const actions = slice.actions;

    try {
      dispatch(actions.startLoading());
      const { data } = await httpClient.get<EventsResult>(`events/active?siteIds=${siteId}`);
      dispatch(actions.updateChamberEvents({ siteId, events: data[siteId] }));
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };

const getEndedChamberEvents =
  (siteId: number): AppThunk =>
  async (dispatch) => {
    const actions = slice.actions;

    try {
      dispatch(actions.startLoading());

      const [{ data: histogram }, { data: events }] = await Promise.all([
        httpClient.get<EventsHistogramType>(`events/site-histogram/${siteId}`),
        httpClient.get<EventsResult>(`events?siteIds=${siteId}`),
      ]);

      dispatch(
        actions.updateChamberEvents({
          siteId: siteId,
          histogram,
          events: events[siteId],
        })
      );
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };

const getLatestEvents = (): AppThunk => async (dispatch) => {
  const actions = slice.actions;
  const activeEventSiteId = Number.parseInt(store.getState().events.activeEntityKey);
  try {
    dispatch(actions.startLoading());
    const { floodEvents, totalCount } = (await httpClient.get<RecentFloodEvents>(`events/recent`)).data;

    if (!Number.isFinite(activeEventSiteId)) {
      dispatch(actions.clearState());
    }

    dispatch(actions.getEntitiesMapSuccess(sortEvents(floodEvents)));
    dispatch(dashboardStats.actions.setTotalOngoingEvents(totalCount));

    if (Number.isFinite(activeEventSiteId)) {
      dispatch(thunks.getActiveChamberEvent(activeEventSiteId));
      dispatch(thunks.getEndedChamberEvents(activeEventSiteId));
      dispatch(thunks.fetchEntities([activeEventSiteId.toString()]));
    }
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const thunks = {
  fetchEntities: fetchEntitiesMap(ModelReducers.events, slice.actions, (ids) =>
    httpClient.get(`events?siteIds=${ids.join('&siteIds=')}`, {
      transformResponse: (httpClient.defaults.transformResponse as AxiosResponseTransformer[]).concat(sortEvents),
    })
  ),

  fetchAll: fetchAllEntitiesMap(ModelReducers.events, slice.actions, () =>
    httpClient.get(`events`, {
      transformResponse: (httpClient.defaults.transformResponse as AxiosResponseTransformer[]).concat(sortEvents),
    })
  ),

  getActiveChamberEvent,
  getEndedChamberEvents,
  getLatestEvents,
};

const selectors = {
  latestEventsSelector,
};

export const eventsEntity = {
  reducer: slice.reducer,
  actions: slice.actions,
  thunks,
  selectors,
} as {
  reducer: typeof slice.reducer;
  actions: typeof slice.actions;
  thunks: typeof thunks;
  selectors: typeof selectors;
};
